import { isSameDay } from 'date-fns';
import { getAllowedOverlapAppointmentId } from '@/lib/work-items/appointmentSideMissions';

export const APPOINTMENT_SCHEDULE_HIDDEN_STATUSES = ['completed', 'done', 'closed', 'cancelled', 'archived'] as const;

type AppointmentAssignee = {
  user_id?: number | null;
};

export interface AppointmentConflictCandidate {
  id: string;
  title?: string | null;
  item_number?: string | null;
  scheduled_start?: string | null;
  scheduled_end?: string | null;
  status?: string | null;
  assigned_to?: number | string | Array<number | null> | null;
  assignees?: AppointmentAssignee[] | null;
  custom_fields?: {
    appointment_execution_mode?: string | null;
    primary_appointment_id?: string | null;
    intentional_overlap?: boolean | null;
    allowed_overlap_with_appointment_id?: string | null;
    [key: string]: unknown;
  } | null;
}

export interface FindAppointmentClashesOptions {
  userId?: number;
  newStart: Date;
  newEnd: Date;
  excludeItemId?: string;
  selectedDate?: Date;
  excludeHiddenStatuses?: boolean;
}

export function isScheduleHiddenAppointmentStatus(status?: string | null): boolean {
  return Boolean(status && APPOINTMENT_SCHEDULE_HIDDEN_STATUSES.includes(status as typeof APPOINTMENT_SCHEDULE_HIDDEN_STATUSES[number]));
}

export function resolvePrimaryAssigneeId(item: AppointmentConflictCandidate): number | null {
  if (item.assignees && item.assignees.length > 0) {
    const assigneeId = item.assignees[0]?.user_id;
    return typeof assigneeId === 'number' && Number.isFinite(assigneeId) ? assigneeId : null;
  }

  if (Array.isArray(item.assigned_to)) {
    const firstAssigned = item.assigned_to.find(
      (value): value is number => typeof value === 'number' && Number.isFinite(value)
    );
    return firstAssigned ?? null;
  }

  if (typeof item.assigned_to === 'string') {
    const parsed = Number(item.assigned_to);
    return Number.isFinite(parsed) ? parsed : null;
  }

  return typeof item.assigned_to === 'number' && Number.isFinite(item.assigned_to)
    ? item.assigned_to
    : null;
}

export function appointmentsOverlap(
  newStart: Date,
  newEnd: Date,
  existingStart: string | Date,
  existingEnd: string | Date
): boolean {
  const normalizedExistingStart = existingStart instanceof Date ? existingStart : new Date(existingStart);
  const normalizedExistingEnd = existingEnd instanceof Date ? existingEnd : new Date(existingEnd);

  if (Number.isNaN(normalizedExistingStart.getTime()) || Number.isNaN(normalizedExistingEnd.getTime())) {
    return false;
  }

  return newStart < normalizedExistingEnd && newEnd > normalizedExistingStart;
}

export function findAppointmentClashes<T extends AppointmentConflictCandidate>(
  items: T[],
  {
    userId,
    newStart,
    newEnd,
    excludeItemId,
    selectedDate,
    excludeHiddenStatuses = true,
  }: FindAppointmentClashesOptions
): T[] {
  return items.filter(item => {
    if (excludeItemId && item.id === excludeItemId) return false;
    if (excludeHiddenStatuses && isScheduleHiddenAppointmentStatus(item.status)) return false;
    if (!item.scheduled_start || !item.scheduled_end) return false;
    if (
      excludeItemId
      && getAllowedOverlapAppointmentId(item) === excludeItemId
      && item.custom_fields?.intentional_overlap !== false
    ) {
      return false;
    }

    if (userId !== undefined && resolvePrimaryAssigneeId(item) !== userId) {
      return false;
    }

    const existingStart = new Date(item.scheduled_start);
    if (selectedDate && !isSameDay(existingStart, selectedDate)) {
      return false;
    }

    return appointmentsOverlap(newStart, newEnd, existingStart, item.scheduled_end);
  });
}